import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME, NEXT_PARAM, safeNext } from '@/auth/routes'
import { looksLikeMobile } from '@/lib/format'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { GoogleButton } from '@/ui/GoogleButton'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/** Mirrors `MAX_FAILED_LOGINS` in `app/security/lockout.py`. Stated, never counted — see below. */
const ATTEMPTS_BEFORE_LOCK = 5

/**
 * `/login` — S-D2 screens 03, 04 and 12.
 *
 * **The attempt counter S-D2 drew is deliberately not rendered, and the reason
 * is the design's own.** Screen 04 says "2 attempts left"; screen 04's rationale
 * says the message must never confirm that an account exists. Those two cannot
 * both hold: a counter is only available for a real account, so showing one for
 * `priya@…` and not for `nobody@…` answers exactly the question the generic
 * message refuses to. What survives is the *goal* — "a real user knows a lockout
 * is coming rather than being surprised by it" — met by stating the policy,
 * which is identical for every response and therefore tells an attacker nothing.
 *
 * `ACCOUNT_LOCKED` is screen 12, with the wait read from the `Retry-After`
 * header the API already sends. That answer *is* only produced for a real
 * account — but only after the attacker has locked it themselves, so it reveals
 * nothing they did not just cause.
 */
export function Login(): JSX.Element {
  const navigate = useNavigate()
  const { adopt } = useAuth()
  const [params] = useSearchParams()
  const next = safeNext(params.get(NEXT_PARAM)) ?? DEFAULT_HOME
  const oauthFailed = params.get('error') === 'google'

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [lockedFor, setLockedFor] = useState<number | null>(null)

  const login = useMutation((body: { email?: string; mobile?: string; password: string }) =>
    call(() => api.POST('/api/v1/auth/login', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const trimmed = identifier.trim()
    const result = await login.run(
      looksLikeMobile(trimmed)
        ? { mobile: trimmed, password }
        : { email: trimmed.toLowerCase(), password },
    )
    if (result.ok) {
      adopt({ user: result.data.user, session: result.data.session })
      void navigate(next, { replace: true })
      return
    }
    setLockedFor(result.failure.code === 'ACCOUNT_LOCKED' ? lockMinutes(result.failure) : null)
  }

  if (lockedFor !== null) return <Locked minutes={lockedFor} />

  return (
    <PublicPage
      title="Welcome back"
      footer={
        <>
          New here?{' '}
          <Link to={`/signup?${NEXT_PARAM}=${encodeURIComponent(next)}`}>Start a free trial</Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <GoogleButton label="Continue with Google" />

          {oauthFailed ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>We could not sign you in with Google</strong>
                Try again, or use your email address and password.
              </div>
            </div>
          ) : null}

          <InlineError failure={login.state.failure} />

          <TextField
            label="Email or mobile number"
            type="text"
            required
            autoFocus
            autoComplete="username"
            inputMode="email"
            help="Use the mobile number in international form, like +919876543210."
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value)
              login.reset()
            }}
          />
          <TextField
            label="Password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              login.reset()
            }}
          />
          <div className="ar-row" style={{ justifyContent: 'space-between' }}>
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
              {ATTEMPTS_BEFORE_LOCK} wrong attempts locks the account for a few minutes.
            </span>
            <Link className="ar-link" to="/forgot-password">
              Forgotten your password?
            </Link>
          </div>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button
            tone="primary"
            size="lg"
            block
            type="submit"
            busy={login.pending}
            busyLabel="Signing in"
          >
            Sign in
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}

/**
 * How long the lock has left, from the header the API already sends.
 *
 * Rounded up, and floored at one: "try again in 0 minutes" reads as a bug, and a
 * lock with 20 seconds left is still a lock.
 */
function lockMinutes(failure: ApiFailure): number {
  return Math.max(1, Math.ceil((failure.retryAfterSeconds ?? 0) / 60))
}

/** S-D2 screen 12. Resetting the password unlocks immediately — the API ends every session. */
function Locked({ minutes }: { minutes: number }): JSX.Element {
  return (
    <PublicPage title="Account temporarily locked">
      <div className="ar-card__body">
        <div className="ar-error-state" role="alert">
          <div className="ar-error-state__icon" aria-hidden="true">
            !
          </div>
          <p className="ar-error-state__title">Too many failed attempts</p>
          <p className="ar-error-state__body">
            You can try again in about <strong>{minutes} minutes</strong>, or reset your password
            now to unlock immediately.
          </p>
          <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/forgot-password">
              Reset password
            </Link>
            <a className="ar-btn ar-btn--ghost ar-btn--sm" href="mailto:support@augrank.com">
              Contact support
            </a>
          </div>
        </div>
      </div>
    </PublicPage>
  )
}
